import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';

import type { FC, ReactNode } from 'react';

export type ViewStackContext = {
  push: (component: ReactNode) => void;
  pop: () => void;
  popable: boolean;
  replace: (component: ReactNode) => void;
  reset: () => void;
};

const ViewStackContext = createContext<ViewStackContext | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its context provider
export const useViewStack = () => {
  const context = useContext(ViewStackContext);

  if (context) {
    return context;
  }

  throw new Error('useViewStack must be used within a ViewStack');
};

type Props = {
  children: ((ctx: ViewStackContext) => ReactNode) | ReactNode;
};

export const ViewStack: FC<Props> = props => {
  const { children } = props;
  const [stack, setStack] = useState<ReactNode[]>([]);
  const viewStackRef = useRef<HTMLDivElement>(null);

  const scrollToTopIfNotInDialog = useCallback(() => {
    const isInDialog =
      viewStackRef.current?.closest('[role="dialog"]') ||
      viewStackRef.current?.closest('[data-radix-dialog-content]');

    if (isInDialog) {
      return;
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }, []);

  const push = useCallback(
    (component: ReactNode) => {
      setStack(prevStack => {
        return [...prevStack, component];
      });
      scrollToTopIfNotInDialog();
    },
    [scrollToTopIfNotInDialog],
  );

  const pop = useCallback(() => {
    setStack(prevStack => {
      return prevStack.slice(0, -1);
    });
    scrollToTopIfNotInDialog();
  }, [scrollToTopIfNotInDialog]);

  const replace = useCallback(
    (component: ReactNode) => {
      setStack(prevStack => {
        return [...prevStack.slice(0, -1), component];
      });
      scrollToTopIfNotInDialog();
    },
    [scrollToTopIfNotInDialog],
  );

  const reset = useCallback(() => {
    setStack([]);
  }, []);

  const contextValue: ViewStackContext = {
    push,
    pop,
    replace,
    reset,
    popable: stack.length > 0,
  };

  const renderChildren = () => {
    return typeof children === 'function' ? children(contextValue) : children;
  };

  return (
    <ViewStackContext.Provider value={contextValue}>
      <div ref={viewStackRef} className="h-full">
        {stack[stack.length - 1] ?? renderChildren()}
      </div>
    </ViewStackContext.Provider>
  );
};
